// /client/src/pages/Admin/DashboardAdherents/CommuneDistributionChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { getCommunityStats } from "../../../services/dashboardServices";
import { FaSearch, FaSortAmountDownAlt, FaSortAmountUpAlt } from "react-icons/fa";

export default function CommuneDistributionChart() {
  const [raw, setRaw] = useState([]);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false); // Top 10 par défaut
  const [sortDir, setSortDir] = useState("desc"); // tri par valeur

  useEffect(() => {
    (async () => {
      try {
        const response = await getCommunityStats();
        // Agrège par commune
        const aggregated = (response || []).reduce((acc, item) => {
          if (!item?.commune) return acc;
          const key = String(item.commune).trim();
          acc[key] = (acc[key] || 0) + Number(item.count || 0);
          return acc;
        }, {});
        const arr = Object.entries(aggregated).map(([name, value]) => ({ name, value }));
        setRaw(arr);
      } catch (e) {
        console.error("Erreur répartition par commune :", e);
        setRaw([]);
      }
    })();
  }, []);

  // Filtre + tri
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? raw.filter((x) => x.name.toLowerCase().includes(q)) : raw.slice();
    base.sort((a, b) => (sortDir === "asc" ? a.value - b.value : b.value - a.value));
    return base;
  }, [raw, query, sortDir]);

  // Données affichées (Top 10 + Autres si besoin)
  const preparedData = useMemo(() => {
    if (showAll || filtered.length <= 10) return filtered;
    const top = filtered.slice(0, 10);
    const others = filtered.slice(10).reduce((s, r) => s + (r.value || 0), 0);
    return [...top, { name: "Autres", value: others }];
  }, [filtered, showAll]);

  const total = useMemo(() => preparedData.reduce((s, r) => s + (r.value || 0), 0), [preparedData]);
  const n = (x) => (x ?? 0).toLocaleString("fr-FR");

  // Palette douce
  const COLORS = [
    "#5B8DEF", "#6EE7B7", "#FCD34D", "#FCA5A5", "#C4B5FD", "#93C5FD",
    "#F9A8D4", "#A7F3D0", "#FDE68A", "#FDBA74", "#DDD6FE", "#BFDBFE",
  ];

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Répartition des adhérents par commune (saison active)
        </h3>
        <div className="mt-2 h-1 w-28 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Barre d’outils */}
      <div className="px-5 sm:px-6 mt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 w-full sm:max-w-md">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrer une commune…"
              className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 ring-1 transition bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            title={`Trier par valeur (${sortDir === "asc" ? "croissant" : "décroissant"})`}
          >
            {sortDir === "asc" ? <FaSortAmountUpAlt /> : <FaSortAmountDownAlt />}
            Trier
          </button>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 ring-1 transition ${
              showAll
                ? "bg-violet-600 text-white ring-violet-400/40"
                : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            }`}
          >
            {showAll ? "Tout afficher" : "Top 10"}
          </button>
        </div>

        <div className="text-sm text-slate-600 dark:text-slate-400">
          Total : <span className="font-semibold">{n(total)}</span>
        </div>
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 mt-4">
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-slate-50/70 dark:bg-slate-900/40">
          <div className="w-full" style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={preparedData}
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {preparedData.map((entry, i) => (
                    <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${n(value)} (${((value / (total || 1)) * 100).toFixed(1)}%)`,
                    name,
                  ]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid rgba(148,163,184,0.3)",
                    background: "rgba(255,255,255,0.95)",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Légende compacte */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {preparedData.map((d, i) => (
              <div
                key={d.name}
                className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
              >
                <span
                  className="inline-block h-3 w-3 rounded"
                  style={{ background: COLORS[i % COLORS.length] }}
                />
                <span className="truncate">{d.name}</span>
                <span className="ml-auto font-medium">{n(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
